import { OrderResponseSchema } from '@lyra/core/api/types/private.order'
import Button from '@lyra/core/components/Button'
import { Order, OrderParamsWithoutSignature, OrderQuote } from '@lyra/web/constants/order'
import { QueryParam, QueryPositionTab } from '@lyra/web/constants/query'
import { OrderQuoteHookParams } from '@lyra/web/hooks/useOrderQuote'
import useSubaccount from '@lyra/web/hooks/useSubaccount'
import useTicker from '@lyra/web/hooks/useTicker'
import { parseMarketFromInstrumentName } from '@lyra/web/utils/instruments'
import { roundUpToStep } from '@lyra/web/utils/number'
import {
  getDefaultOrderExpiryDays,
  getSignatureExpirySecsForExpiryDays,
} from '@lyra/web/utils/order'
import { parseAsStringEnum, useQueryState } from 'next-usequerystate'
import { useCallback, useState } from 'react'

import TradeErrorNotice from '../TradeErrorNotice'
import TradeExecuteButtonRow from '../TradeExecuteButtonRow'

type Props = {
  quoteParams: OrderQuoteHookParams
  quote?: OrderQuote | null
  error?: Error | null
  isLoading?: boolean
  onSubmitOrder?: (order: Order) => void
  editOrderId?: string
}

export default function TradeFormSubmitOrderButton({
  quoteParams,
  quote,
  error: quoteError,
  isLoading,
  onSubmitOrder,
  editOrderId,
}: Props) {
  const { subaccount, submitOrder, replaceOrder } = useSubaccount()

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [orderError, setOrderError] = useState<Error>()

  const instrumentName = quoteParams.instrument_name

  const error = orderError ?? quoteError

  const [_0, setPositionTab] = useQueryState(
    QueryParam.PositionTab,
    parseAsStringEnum(Object.values(QueryPositionTab))
  )

  const ticker = useTicker(instrumentName)

  const handleSubmitOrder = useCallback(async () => {
    setOrderError(undefined)
    setIsSubmitting(true)
    try {
      if (!ticker) {
        throw new Error('Ticker not loaded')
      }

      if (!subaccount) {
        throw new Error('No subaccount')
      }

      if (!quote) {
        throw new Error('No quote')
      }

      const marketId = parseMarketFromInstrumentName(quoteParams.instrument_name)
      if (!marketId) {
        throw new Error('No market')
      }

      const max_fee = roundUpToStep(+quote.suggested_max_fee, +ticker.tick_size)

      const { expiryDays: _expiryDays, ...tmpQuoteParams } = quoteParams
      const expiryDays = _expiryDays ? _expiryDays : getDefaultOrderExpiryDays(quoteParams)
      const signature_expiry_sec = getSignatureExpirySecsForExpiryDays(expiryDays)

      const orderParams: OrderParamsWithoutSignature = {
        ...tmpQuoteParams,
        signature_expiry_sec,
        subaccount_id: subaccount.subaccount_id,
        max_fee: max_fee.toString(),
      }

      console.debug('orderParams', orderParams)

      let order: OrderResponseSchema | null | undefined
      if (editOrderId) {
        const result = await replaceOrder(editOrderId, ticker, orderParams)
        order = result.order
      } else {
        order = await submitOrder(ticker, orderParams)
      }

      if (!order) {
        console.warn('No order in response')
        return
      }

      if (order.order_status === 'filled') {
        // open positions tab on instantly filled order
        setPositionTab(QueryPositionTab.Positions)
      } else if (order.order_status === 'open') {
        // open orders tab on open (limit) order
        setPositionTab(QueryPositionTab.Orders)
      } else if (order.order_status === 'cancelled') {
        throw new Error('Order cancelled')
      }

      if (onSubmitOrder) {
        onSubmitOrder(order)
      }
    } catch (error) {
      console.error(error)
      if (error instanceof Error) {
        setOrderError(error)
      }
    } finally {
      setIsSubmitting(false)
    }
  }, [
    quoteParams,
    quote,
    ticker,
    subaccount,
    editOrderId,
    onSubmitOrder,
    submitOrder,
    replaceOrder,
    setPositionTab,
  ])

  const isZeroLimitPrice = +quoteParams.limit_price === 0
  const isZeroAmount = +quoteParams.amount === 0
  const isTriggerOrderWithZeroTriggerPrice =
    !!quoteParams.trigger_type && !!quoteParams.trigger_price && +quoteParams.trigger_price === 0

  const orderButtonMessage =
    quote && !quote.is_valid
      ? 'Insufficient Balance'
      : isTriggerOrderWithZeroTriggerPrice
      ? 'Enter Trigger Price'
      : isZeroLimitPrice
      ? 'Enter Price'
      : isZeroAmount
      ? 'Enter Amount'
      : editOrderId
      ? 'Edit Order'
      : 'Submit Order'

  const isDisabled =
    !quote?.is_valid ||
    !!quoteError ||
    isZeroLimitPrice ||
    isZeroAmount ||
    isTriggerOrderWithZeroTriggerPrice

  const hideError = isZeroLimitPrice || isZeroAmount || isTriggerOrderWithZeroTriggerPrice

  return (
    <TradeExecuteButtonRow
      isInsufficientBalance={!!quote && !quote.is_valid}
      insufficientBalanceMessage={quote?.invalid_reason}
    >
      {error && !hideError ? <TradeErrorNotice error={error} /> : null}
      <Button
        isDisabled={isDisabled}
        onPress={handleSubmitOrder}
        size="lg"
        isSolid
        color="cta"
        label={!isLoading ? orderButtonMessage : null}
        width="100%"
        isLoading={isSubmitting || isLoading || !ticker}
      />
    </TradeExecuteButtonRow>
  )
}
